import React, { useState, useMemo } from 'react';
import { Link, graphql, useStaticQuery } from 'gatsby';
import {
  ChevronDoubleLeftIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDoubleRightIcon,
} from '@heroicons/react/solid';
import {
  useTable,
  usePagination,
  useFilters,
  useSortBy,
  useGlobalFilter,
  useAsyncDebounce,
} from 'react-table';
import { matchSorter } from 'match-sorter';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
dayjs.extend(duration);

import Layout from '../../components/layout';
import Button from '../../components/button';
import SEO from '../../components/seo';

import { secondsToHHMMSS } from '../../utils/seconds-to-hhmmss';
import { formatNumber } from '../../utils/format-number';

// Custom component to render categories
const Categories = ({ values }) => {
  return (
    <span className="flex flex-wrap gap-2">
      {values
        .slice(0, 3)
        .sort()
        .map((category, idx) => {
          return (
            <Link
              to={`/categories/${category.replace(/^\w+_/, '')}`}
              key={idx}
              className="text-sm border px-1 border-gray-500 rounded-md"
            >
              {category.replace(/^\w+_/, '')}
            </Link>
          );
        })}
    </span>
  );
};

// Default UI for global filtering
function GlobalFilter({
  preGlobalFilteredRows,
  globalFilter,
  setGlobalFilter,
}) {
  const count = preGlobalFilteredRows.length;
  const [value, setValue] = useState(globalFilter);
  const onChange = useAsyncDebounce((value) => {
    setGlobalFilter(value || undefined);
  }, 200);

  return (
    <span>
      <input
        value={value || ''}
        onChange={(e) => {
          setValue(e.target.value);
          onChange(e.target.value);
        }}
        placeholder={`Search ${count} record${count === 1 ? '' : 's'}`}
        className="border-0"
      />
    </span>
  );
}

// Default UI for filtering
function DefaultColumnFilter({
  column: { filterValue, preFilteredRows, setFilter },
}) {
  const count = preFilteredRows.length;

  return (
    <input
      value={filterValue || ''}
      onChange={(e) => {
        setFilter(e.target.value || undefined); // Set undefined to remove the filter entirely
      }}
      placeholder={`Search ${count} record${count === 1 ? '' : 's'}`}
      className="mt-2 w-60"
    />
  );
}

// Custom filters
function SelectColumnFilter({
  column: { filterValue, setFilter, preFilteredRows, id },
}) {
  const options = useMemo(() => {
    const options = new Set();
    preFilteredRows.forEach((row) => {
      options.add(row.values[id]);
    });
    return [...options.values()];
  }, [id, preFilteredRows]);

  return (
    <select
      value={filterValue}
      onChange={(e) => {
        setFilter(e.target.value || undefined);
      }}
    >
      <option value="">All</option>
      {options.map((option, i) => (
        <option key={i} value={option}>
          {option}
        </option>
      ))}
    </select>
  );
}

function SliderColumnFilter({
  column: { filterValue, setFilter, preFilteredRows, id },
}) {
  const [min, max] = useMemo(() => {
    let min = preFilteredRows.length ? preFilteredRows[0].values[id] : 0;
    let max = preFilteredRows.length ? preFilteredRows[0].values[id] : 0;
    preFilteredRows.forEach((row) => {
      min = Math.min(row.values[id], min);
      max = Math.max(row.values[id], max);
    });
    return [min, max];
  }, [id, preFilteredRows]);

  return (
    <>
      <input
        type="range"
        min={min}
        max={max}
        value={filterValue || min}
        onChange={(e) => {
          setFilter(parseInt(e.target.value, 10));
        }}
        className="mt-1 w-32"
      />
      <Button variant="small" onClick={() => setFilter(undefined)}>
        Off
      </Button>
    </>
  );
}

function fuzzyTextFilterFn(rows, id, filterValue) {
  return matchSorter(rows, filterValue, { keys: [(row) => row.values[id]] });
}

// Let the table remove the filter if the string is empty
fuzzyTextFilterFn.autoRemove = (val) => !val;

// Page component
const SortEpisodesPage = () => {
  const episodesData = useStaticQuery(graphql`
    query {
      allEpisodesJson {
        edges {
          node {
            id
            guest
            publishedAt
            duration
            gender
            categories
            fields {
              slug
              durationSeconds
            }
          }
        }
      }
    }
  `);

  const filterTypes = useMemo(
    () => ({
      // Add a new fuzzyTextFilterFn filter type.
      fuzzyText: fuzzyTextFilterFn,
      // Or, override the default text filter to use
      // "startWith"
      text: (rows, id, filterValue) => {
        return rows.filter((row) => {
          const rowValue = row.values[id];
          return rowValue !== undefined
            ? String(rowValue)
                .toLowerCase()
                .startsWith(String(filterValue).toLowerCase())
            : true;
        });
      },
    }),
    []
  );

  const defaultColumn = useMemo(
    () => ({
      Filter: DefaultColumnFilter,
    }),
    []
  );

  const data = useMemo(() => episodesData.allEpisodesJson.edges, []);
  const columns = useMemo(
    () => [
      {
        Header: 'ID',
        accessor: 'node.id',
        Cell: (cellInfo) => (
          <Link
            to={`/episodes/${
              cellInfo.data[cellInfo.row.index].node.fields.slug
            }`}
            className="text-gradient"
          >
            {cellInfo.data[cellInfo.row.index].node.id}
          </Link>
        ),
      },
      {
        Header: 'Guest(s)',
        accessor: 'node.guest',
      },
      {
        Header: 'Published',
        accessor: 'node.publishedAt',
        Cell: ({ cell: { value } }) => dayjs(value).format('MMM D, YYYY'),
      },
      {
        Header: 'Duration',
        accessor: 'node.fields.durationSeconds',
        Cell: ({ cell: { value } }) => secondsToHHMMSS(value),
        Filter: SliderColumnFilter,
        filter: filterGreaterThan,
      },
      {
        Header: 'Gender',
        accessor: 'node.gender',
        Filter: SelectColumnFilter,
        filter: 'includes',
      },
      {
        Header: 'categories',
        accessor: 'node.categories',
        Cell: ({ cell: { value } }) => <Categories values={value} />,
      },
    ],
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    state,
    // Pagination
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize },
    // Filter
    visibleColumns,
    setGlobalFilter,
    preGlobalFilteredRows,
  } = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: 0, pageSize: 10 },
      defaultColumn,
      filterTypes,
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  return (
    <Layout>
      <SEO title="Sort and filter episodes" />
      <h2>Sort/Filter episodes</h2>
      <details>
        <summary>
          <h3 className="inline text-xl">Usage</h3>
        </summary>
        <ul className="text-lg mb-1 ml-1 border-l border-gray-500 pl-4 list-disc list-inside leading-loose">
          <li>
            Click on table headers to{' '}
            <span className="text-hl">sort episodes</span> by specific fields in
            ascending or descending order.
          </li>
          <li>
            Use the <span className="text-hl">global filter</span> in the second
            row to filter any field across all records.
          </li>
          <li>
            To use the <span className="text-hl">slider filter</span> for
            duration, first click on the header "DURATION" to sort episodes in
            ascending order of duration. Then, use the slider.
          </li>
          <li>
            To <span className="text-hl">filter episodes by month</span>, enter
            date in "mm-dd" format, for example, '3-14' to search for episodes
            released on Pi day.
          </li>
        </ul>
      </details>
      <div className="mt-4 flex flex-col px-1">
        <div className="-my-2 overflow-x-auto">
          <div className="align-middle inline-block min-w-full">
            <div className="shadow overflow-hidden border border-gray-600 rounded-lg">
              <table
                {...getTableProps()}
                className="min-w-full divide-y divide-gray-200 border"
              >
                <thead>
                  {headerGroups.map((headerGroup) => (
                    <tr {...headerGroup.getHeaderGroupProps()}>
                      {headerGroup.headers.map((column) => (
                        <th
                          {...column.getHeaderProps()}
                          className="px-4 py-1 text-left"
                        >
                          <span
                            {...column.getSortByToggleProps()}
                            className="uppercase"
                          >
                            {column.render('Header')}
                            <span>
                              {column.isSorted
                                ? column.isSortedDesc
                                  ? ' ⇩'
                                  : ' ⇧'
                                : ''}
                            </span>
                          </span>
                          <div>
                            {column.canFilter ? column.render('Filter') : null}
                          </div>
                        </th>
                      ))}
                    </tr>
                  ))}
                  <tr>
                    <th
                      colSpan={visibleColumns.length}
                      className="px-4 text-left py-3"
                    >
                      <GlobalFilter
                        preGlobalFilteredRows={preGlobalFilteredRows}
                        globalFilter={state.globalFilter}
                        setGlobalFilter={setGlobalFilter}
                      />
                    </th>
                  </tr>
                </thead>
                <tbody
                  {...getTableBodyProps()}
                  className="divide-y divide-gray-700"
                >
                  {page.map((row) => {
                    prepareRow(row);

                    return (
                      <tr {...row.getRowProps()} className="py-3">
                        {row.cells.map((cell) => {
                          return (
                            <td
                              {...cell.getCellProps()}
                              className="px-4 py-2 whitespace-nowrap"
                            >
                              {cell.render('Cell')}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>{' '}
            </div>
          </div>
        </div>
      </div>

      {/* Pagination */}
      <div className="py-6 px-1 flex items-center justify-between">
        <div className="flex-1 flex justify-between sm:hidden">
          <Button onClick={() => previousPage()} disabled={!canPreviousPage}>
            Previous
          </Button>
          <select
            value={state.pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
            }}
          >
            {[10, 25, 50, 100].map((pageSize) => (
              <option key={pageSize} value={pageSize}>
                Show {pageSize}
              </option>
            ))}
          </select>
          <Button onClick={() => nextPage()} disabled={!canNextPage}>
            Next
          </Button>
        </div>
        <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
          <div className="flex gap-x-2">
            <span className="text-sm text-gray-400">
              Page <span className="font-medium">{state.pageIndex + 1}</span> of{' '}
              <span className="font-medium">{pageOptions.length}</span>
            </span>
            <select
              value={state.pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
              }}
            >
              {[10, 25, 50, 100].map((pageSize) => (
                <option key={pageSize} value={pageSize}>
                  Show {pageSize}
                </option>
              ))}
            </select>
          </div>
          <span>
            Go to page:{' '}
            <input
              type="number"
              defaultValue={pageIndex + 1}
              onChange={(e) => {
                const page = e.target.value ? Number(e.target.value) - 1 : 0;
                gotoPage(page);
              }}
              className="w-24"
            />
          </span>{' '}
          <div className="hidden sm:block">
            <nav
              className="inline-flex gap-1 rounded-md shadow-sm"
              aria-label="Pagination"
            >
              <Button onClick={() => gotoPage(0)} disabled={!canPreviousPage}>
                <span className="sr-only">First</span>
                <ChevronDoubleLeftIcon className="h-5 w-5" aria-hidden="true" />
              </Button>
              <Button
                onClick={() => previousPage()}
                disabled={!canPreviousPage}
              >
                <span className="sr-only">Previous</span>
                <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
              </Button>
              <Button onClick={() => nextPage()} disabled={!canNextPage}>
                <span className="sr-only">Next</span>
                <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
              </Button>
              <Button
                onClick={() => gotoPage(pageCount - 1)}
                disabled={!canNextPage}
              >
                <span className="sr-only">Last</span>
                <ChevronDoubleRightIcon
                  className="h-5 w-5"
                  aria-hidden="true"
                />
              </Button>
            </nav>
          </div>
        </div>
      </div>
    </Layout>
  );
};

// Define a custom filter filter function!
function filterGreaterThan(rows, id, filterValue) {
  return rows.filter((row) => {
    const rowValue = row.values[id];
    return rowValue >= filterValue;
  });
}

// This is an autoRemove method on the filter function that
// when given the new filter value and returns true, the filter
// will be automatically removed. Normally this is just an undefined
// check, but here, we want to remove the filter if it's not a number
filterGreaterThan.autoRemove = (val) => typeof val !== 'number';

export default SortEpisodesPage;
